import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../../../convex/_generated/api";
import { Id } from "../../../../../../convex/_generated/dataModel";
import { useToast } from "@/components/ui/use-toast";
import { X } from "lucide-react";

interface Project {
  _id: Id<"locationProjects">;
  name: string;
  description?: string;
  status: "In Progress" | "Scheduled" | "Completed";
  startDate: string;
  dueDate: string;
  assignedTo: Id<"employees">[];
  assignedToNames?: string[];
  locationId: Id<"serviceLocations">;
  createdAt: number;
  updatedAt: number;
}

interface EditProjectSheetProps {
  isOpen: boolean;
  onClose: () => void;
  project: Project | null;
  onProjectUpdated?: () => void;
}

export function EditProjectSheet({
  isOpen,
  onClose,
  project,
  onProjectUpdated,
}: EditProjectSheetProps) {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    status: "",
    startDate: "",
    dueDate: "",
    assignedTo: [] as string[],
    assignedSubcontractors: [] as string[],
  });
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedSubcontractor, setSelectedSubcontractor] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  // Fetch active employees and subcontractors for assignment
  const employees = useQuery(api.employees.getActiveEmployees) ?? [];
  const subcontractors = useQuery(api.subcontractors.getSubcontractors) ?? [];

  // Update project mutation
  const updateProject = useMutation(api.serviceLocations.updateProject);

  // Initialize form data when project changes
  useEffect(() => {
    if (project) {
      setFormData({
        name: project.name,
        description: project.description || "",
        status: project.status,
        startDate: project.startDate,
        dueDate: project.dueDate,
        assignedTo: project.assignedTo.map((id) => id.toString()),
        assignedSubcontractors:
          (project as any).assignedSubcontractors?.map((id: any) => id.toString()) || [],
      });
    }
  }, [project]);

  // Handle form input changes
  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Add employee to assigned list
  const handleAddEmployee = () => {
    if (selectedEmployee && !formData.assignedTo.includes(selectedEmployee)) {
      setFormData((prev) => ({
        ...prev,
        assignedTo: [...prev.assignedTo, selectedEmployee],
      }));
      setSelectedEmployee("");
    }
  };

  // Remove employee from assigned list
  const handleRemoveEmployee = (employeeId: string) => {
    setFormData((prev) => ({
      ...prev,
      assignedTo: prev.assignedTo.filter((id) => id !== employeeId),
    }));
  };

  // Add subcontractor to assigned list
  const handleAddSubcontractor = () => {
    if (selectedSubcontractor && !formData.assignedSubcontractors.includes(selectedSubcontractor)) {
      setFormData((prev) => ({
        ...prev,
        assignedSubcontractors: [...prev.assignedSubcontractors, selectedSubcontractor],
      }));
      setSelectedSubcontractor("");
    }
  };

  // Remove subcontractor from assigned list
  const handleRemoveSubcontractor = (subcontractorId: string) => {
    setFormData((prev) => ({
      ...prev,
      assignedSubcontractors: prev.assignedSubcontractors.filter((id) => id !== subcontractorId),
    }));
  };

  // Get employee name by ID
  const getEmployeeName = (employeeId: string) => {
    const employee = employees.find((emp: any) => emp._id === employeeId);
    return employee?.name || "Unknown Employee";
  };

  // Get subcontractor name by ID
  const getSubcontractorName = (subcontractorId: string) => {
    const subcontractor = subcontractors.find((sub: any) => sub._id === subcontractorId);
    return subcontractor?.name || "Unknown Subcontractor";
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!project) return;

    // Validation
    if (!formData.name.trim()) {
      toast({
        title: "Error",
        description: "Project name is required",
        variant: "destructive",
      });
      return;
    }

    if (!formData.status) {
      toast({
        title: "Error",
        description: "Project status is required",
        variant: "destructive",
      });
      return;
    }

    if (!formData.startDate) {
      toast({
        title: "Error",
        description: "Start date is required",
        variant: "destructive",
      });
      return;
    }

    if (!formData.dueDate) {
      toast({
        title: "Error",
        description: "Due date is required",
        variant: "destructive",
      });
      return;
    }

    if (formData.assignedTo.length === 0 && formData.assignedSubcontractors.length === 0) {
      toast({
        title: "Error",
        description: "At least one employee or subcontractor must be assigned",
        variant: "destructive",
      });
      return;
    }

    // Validate dates
    const startDate = new Date(formData.startDate);
    const dueDate = new Date(formData.dueDate);

    if (dueDate <= startDate) {
      toast({
        title: "Error",
        description: "Due date must be after start date",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await updateProject({
        projectId: project._id,
        name: formData.name,
        description: formData.description || undefined,
        status: formData.status as "In Progress" | "Scheduled" | "Completed",
        startDate: formData.startDate,
        dueDate: formData.dueDate,
        assignedTo: formData.assignedTo as Id<"employees">[],
        assignedSubcontractors:
          formData.assignedSubcontractors.length > 0
            ? (formData.assignedSubcontractors as Id<"subcontractors">[])
            : undefined,
      });

      toast({
        title: "Success!",
        description: "Project updated successfully",
      });

      // Call the callback to refresh data
      if (onProjectUpdated) {
        onProjectUpdated();
      }

      onClose();
    } catch (error) {
      console.error("Error updating project:", error);
      toast({
        title: "Error",
        description: "Failed to update project",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form when sheet closes
  const handleClose = () => {
    if (project) {
      setFormData({
        name: project.name,
        description: project.description || "",
        status: project.status,
        startDate: project.startDate,
        dueDate: project.dueDate,
        assignedTo: project.assignedTo.map((id) => id.toString()),
        assignedSubcontractors:
          (project as any).assignedSubcontractors?.map((id: any) => id.toString()) || [],
      });
    }
    setSelectedEmployee("");
    setSelectedSubcontractor("");
    onClose();
  };

  // Get today's date in YYYY-MM-DD format for min date
  const today = new Date().toISOString().split("T")[0];

  if (!project) return null;

  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent className="w-[500px] sm:max-w-[500px]">
        <SheetHeader>
          <SheetTitle>Edit Project</SheetTitle>
          <SheetDescription>Update the project details for this service location.</SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-180px)] pr-4">
          <div className="space-y-6 px-2 py-6">
            {/* Project Name */}
            <div className="space-y-2">
              <Label htmlFor="name">Project Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                placeholder="Enter project name"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleInputChange("description", e.target.value)}
                placeholder="Enter project description"
                rows={3}
              />
            </div>

            {/* Status */}
            <div className="space-y-2">
              <Label htmlFor="status">Status *</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => handleInputChange("status", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Scheduled">Scheduled</SelectItem>
                  <SelectItem value="In Progress">In Progress</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Start Date */}
            <div className="space-y-2">
              <Label htmlFor="startDate">Start Date *</Label>
              <Input
                id="startDate"
                type="date"
                value={formData.startDate}
                onChange={(e) => handleInputChange("startDate", e.target.value)}
                min={today}
              />
            </div>

            {/* Due Date */}
            <div className="space-y-2">
              <Label htmlFor="dueDate">Due Date *</Label>
              <Input
                id="dueDate"
                type="date"
                value={formData.dueDate}
                onChange={(e) => handleInputChange("dueDate", e.target.value)}
                min={formData.startDate || today}
              />
            </div>

            {/* Assign Staff */}
            <div className="space-y-4">
              <Label>Assign Staff *</Label>

              <div className="flex gap-2">
                <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Select an employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees
                      .filter((emp: any) => !formData.assignedTo.includes(emp._id))
                      .map((employee: any) => (
                        <SelectItem key={employee._id} value={employee._id}>
                          {employee.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  onClick={handleAddEmployee}
                  disabled={!selectedEmployee}
                  variant="outline"
                >
                  Add Staff
                </Button>
              </div>

              {/* Assigned Staff List */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Assigned Staff:</Label>
                {formData.assignedTo.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {formData.assignedTo.map((employeeId) => (
                      <Badge
                        key={employeeId}
                        variant="secondary"
                        className="flex items-center gap-1 pr-1"
                      >
                        {getEmployeeName(employeeId)}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-4 w-4 p-0 hover:bg-destructive hover:text-destructive-foreground"
                          onClick={() => handleRemoveEmployee(employeeId)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-3 text-center text-sm text-muted-foreground">
                    No staff assigned yet. Select an employee above and click "Add Staff" to assign
                    them.
                  </div>
                )}
              </div>
            </div>

            {/* Assign Subcontractors */}
            <div className="space-y-4">
              <Label>Assign Subcontractors</Label>

              <div className="flex gap-2">
                <Select value={selectedSubcontractor} onValueChange={setSelectedSubcontractor}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Select a subcontractor" />
                  </SelectTrigger>
                  <SelectContent>
                    {subcontractors
                      .filter((sub: any) => !formData.assignedSubcontractors.includes(sub._id))
                      .map((subcontractor: any) => (
                        <SelectItem key={subcontractor._id} value={subcontractor._id}>
                          {subcontractor.name} - {subcontractor.contactName}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  onClick={handleAddSubcontractor}
                  disabled={!selectedSubcontractor}
                  variant="outline"
                >
                  Add Subcontractor
                </Button>
              </div>

              {/* Assigned Subcontractors List */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Assigned Subcontractors:</Label>
                {formData.assignedSubcontractors.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {formData.assignedSubcontractors.map((subcontractorId) => (
                      <Badge
                        key={subcontractorId}
                        variant="secondary"
                        className="flex items-center gap-1 bg-green-100 pr-1 text-green-800"
                      >
                        {getSubcontractorName(subcontractorId)}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-4 w-4 p-0 hover:bg-destructive hover:text-destructive-foreground"
                          onClick={() => handleRemoveSubcontractor(subcontractorId)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-3 text-center text-sm text-muted-foreground">
                    No subcontractors assigned yet. Select a subcontractor above and click "Add
                    Subcontractor" to assign them.
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={handleClose}
                className="flex-1"
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                className="flex-1"
                disabled={
                  isSubmitting ||
                  !formData.name ||
                  !formData.status ||
                  !formData.startDate ||
                  !formData.dueDate ||
                  formData.assignedTo.length === 0
                }
              >
                {isSubmitting ? "Updating..." : "Update Project"}
              </Button>
            </div>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
